import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import AKForm, { RequiredAsterisk, type FormApi } from '@atlaskit/form';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { PlatformClassificationTagAri } from '@atlassian/ari/platform/classification-tag';
import {
	PRODUCT_DISCOVERY_PROJECT,
	SERVICE_DESK_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { getBoardId } from '@atlassian/jira-common-navigation/src/get-scope-data/index.tsx';
import { useCrossJoinThirdPartyNudgeExperiment } from '@atlassian/jira-cross-join-third-party-nudges/src/ui/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { ValidationError, isValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { submitForgeFormField } from '@atlassian/jira-forge-controllers-inline-fields-state/src/actions/utils/dispatch-form-submit-and-blur-elements/index.tsx';
import {
	FAILED_FIELD_STATE,
	FOCUSED_FIELD_STATE,
	FORGE_CF_TIMEOUT,
	FORGE_STATUS_PROCEED,
	FORGE_STATUS_TIMEOUT,
	FORGE_STATUS_WAIT,
	IDLE_FIELD_STATE,
	PENDING_FIELD_STATE,
	STORED_FIELD_STATE,
} from '@atlassian/jira-forge-controllers-inline-fields-state/src/constants.tsx';
import { useInlineFields } from '@atlassian/jira-forge-controllers-inline-fields-state/src/index.tsx';
import type {
	InlineFieldState,
	InlineFieldsState,
} from '@atlassian/jira-forge-controllers-inline-fields-state/src/types.tsx';
import {
	focusFormButton,
	isForgeCustomField,
} from '@atlassian/jira-forge-field-base/src/utils/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	UIM_CONSENT_REQUIRED,
	useUIModificationsConsentStatus,
	useUiModificationsEnabledStatus,
} from '@atlassian/ui-modifications-core/controllers';
import { REQUEST_TYPE } from '@atlassian/jira-issue-create-common-types/src/common/constants/graphql-field-types.tsx';
import {
	DEFAULT_SCROLL_OPTIONS,
	FORM_DROPZONE_CONTAINER_ID,
	ISSUE_CREATE_TYPE,
} from '@atlassian/jira-issue-create-common-types/src/common/constants/index.tsx';
import type {
	FormError,
	IssueOrRequestType,
	Project,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { Divider } from '@atlassian/jira-issue-create-commons/src/common/ui/fields/styled.tsx';
import IssueCreateLoader from '@atlassian/jira-issue-create-commons/src/common/ui/loader/index.tsx';
import DataClassificationField from '@atlassian/jira-issue-create-commons/src/ui/data-classification-field/index.tsx';
import { useClassificationState } from '@atlassian/jira-issue-create-commons/src/ui/data-classification-field/utils.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { useGICLifeCycleEvents } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import {
	useCreateFormState,
	useFormContext,
} from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { LinkedAlertsField } from '@atlassian/jira-issue-field-linked-alerts/src/index.tsx';
import type { StatusTransition } from '@atlassian/jira-issue-field-status/src/common/types.tsx';
import { FormApiProvider } from '@atlassian/jira-issue-form-api/src/controllers/index.tsx';
import { canConfigureIssueTypeForProject } from '@atlassian/jira-issue-type-config-common/src/permissions.tsx';
import { canConfigureStatusForProject } from '@atlassian/jira-status-config-common/src/permissions.tsx';
import { LIST_OF_CHILD_ISSUES_TRIGGER_POINTS } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/constants.tsx';
import { useSitePickerContext } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/controllers/use-site-picker-context/index.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import type { TriggerPointKeyType } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/types.tsx';
import { useIsEmbedded } from '@atlassian/jira-platform-react-hooks-use-is-embedded/src/common/utils/index.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import {
	MountEvent,
	fireOperationalAnalyticsDeferred,
	fireTrackAnalyticsDeferred,
	useAnalyticsEvents,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import { getApplicationForProject } from '@atlassian/jira-shared-types/src/application.tsx';
import { getEditionForProject } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useStorageEnforcement } from '@atlassian/jira-software-enforce-storage-limits/src/controllers/storage-enforcement/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { ISSUE_CREATE_FORM } from '../../../common/constants/selectors.tsx';
import type { ResetForm } from '../../../common/types/index.tsx';
import { useIsGICPositioningEnabled } from '../../../controllers/gic-positioning-enabled/main.tsx';
import { useInteractedFields } from '../../../controllers/use-interacted-fields/index.tsx';
import { useInvitePrompt } from '../../../controllers/use-invite-prompt/index.tsx';
import useCreateIssueService from '../../../services/create-issue/index.tsx';
import type {
	CreateIssuePayload,
	IssueCreateSuccessProps,
	TelemetryAttributes,
} from '../../../services/create-issue/types.tsx';
import { useDescriptionAnalytics } from '../../../services/use-description-analytics/index.tsx';
import { ActionsContainer } from './actions-container/index.tsx';
import { ExternalControlsRenderer } from './external-controls-renderer/index.tsx';
import LayoutRenderer from './layout-renderer/index.tsx';
import messages from './messages.tsx';
import { ProformaBanner } from './proforma-banner/index.tsx';
import ProjectPicker from './project-picker/index.tsx';
import { RequestTypeFieldsLayoutPreference } from './request-type-fields-layout-preference/index.tsx';
import SectionMessages from './section-messages/index.tsx';
import SitePicker from './site-picker/index.tsx';
import { SmartFieldMessage } from './smart-field-message/index.tsx';
import StatusField from './status-field/index.tsx';
import TypePicker from './type-picker/index.tsx';
import type { CreateFormProps, OnCreateFailure, OnCreateSuccess } from './types.tsx';
import {
	canSetContextForSubmit,
	getAnalyticsIssueType,
	getContextFieldsForSubmit,
	getParentIdForSubmit,
	getQueryVariables,
	getUserInputForRetention,
	isDisplayConditionCFCausingError,
	isWatchersUsed,
	resetFormFieldsForCreateAnother,
	shouldShowLinkedAlertsField,
	shouldShowStatusField,
	transformInlineErrorMessage,
	transformValidationErrors,
	useIsCurrentUserAdminOfProject,
	getAssigneeType,
} from './utils.tsx';

function isChildIssueTriggerPoint(
	key: TriggerPointKeyType,
): key is (typeof LIST_OF_CHILD_ISSUES_TRIGGER_POINTS)[number] {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	return LIST_OF_CHILD_ISSUES_TRIGGER_POINTS.includes(key as any);
}

const CreateFormBase = (props: CreateFormProps) => {
	const [
		{
			sectionMessage,
			payload: triggerIssueCreatePayload,
			alertsToBeLinked,
			isSubTaskCreationOpen,
			disableProjectDropdown,
			disableIssueTypeDropdown,
			displayHeaderActionContainer,
			displaySitePicker,
			context,
			triggerPointKey,
			isCreateLinkedIssue,
			isModalLoadInContext,
			wasStateRestored,
			minimizableModalView,
			submitErrorMessage: presetSubmitErrorMessage,
			overrideUseRequestTypeFieldsToggleState,
			isMiniDesignEnabled,
			sessionId,
			ExternalControls,
			optOutSmartFieldIds,
		},
		{ clearSectionMessage, removeAlertFromAlertsToBeLinked },
	] = useTriggerIssueCreateModal();
	const {
		onIssueCreateSuccess,
		canCreateAnotherIssue = false,
		isLoading,
		fieldConfig,
		fieldTabs,
		contextFields,
		filteredOutModules,
		meta,
		error,
		apiLatency,
		onFetchConfig: fetchConfig,
		dirtyFieldsCurrentSession,
		getFormState,
		onFormFieldsReset,
		statusFieldProps,
		classificationFieldProps,
		proformaConfiguration,
		watchersConfigurations,
		onResetApolloCache,
		onStatusCreatedOrUpdated,
		onSiteSelection,
		onProjectSelection,
	} = props;
	const isEmbedded = useIsEmbedded();
	const isMini = minimizableModalView === 'mini' || isMiniDesignEnabled;
	const isGICPositioningEnabled = useIsGICPositioningEnabled();
	// TODO Add this field with the fields on previous line when cleaning FF
	const fieldPropsExtensions = isGICPositioningEnabled() ? props.fieldPropsExtensions : undefined;

	const {
		data: { user: currentUser },
	} = useCurrentUser();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();
	const { publish } = useGICLifeCycleEvents();
	const { interactedFields } = useInteractedFields();

	const appEditions = useAppEditions();

	const [
		{
			watchers,
			ecosystem: {
				forge: { hasFinishedLoading },
			},
			isSmartFieldsToggled,
		},
		{ setIsForgeFieldsDetected, resetExpandedCollapsedArea, setIsSmartFieldsToggled },
	] = useFormContext();

	const uiModificationsStatus = useUiModificationsEnabledStatus(hasFinishedLoading);

	const [
		{ selectedProject, selectedIssueType, meta: formMeta, areConfiguredFieldsModified },
		{
			setSelectedProject,
			setSelectedIssueType,
			setRequestBackedIssueType,
			setIsEpicLinkSelected,
			setSubmitting,
			setMeta,
			setSubmitErrorMessage,
			setSubmitFieldwiseErrors,
			setDataFetchError,
			setInlineErrorMessage,
			setIsFormDirty,
			setAreConfiguredFieldsModified,
			setCurrentActiveTabIndex,
			setResetFormAction,
			setShouldAutoFocus,
		},
	] = useCreateFormState();
	const [{ selectedCloudId }] = useSitePickerContext();
	const [createIssue] = useCreateIssueService(selectedCloudId);
	const [fields, { dispatchFormSubmits, resetState }] = useInlineFields();
	// This useRef needs to be first in order for shouldUseRequestTypeDefault tests to work
	const shouldUseRequestTypeDefault = useRef(true);

	const retainedFieldsData = useRef<{ mutationGroup: string; fieldId: string }[]>([]);

	const [forgeFieldBlockStatus, setForgeFieldBlockStatus] = useState(FORGE_STATUS_PROCEED);
	const { formatMessage, formatList } = useIntl();

	const [hasCreatedAnotherIssue, setHasCreatedAnotherIssue] = useState(false);
	const { initialClassification } = classificationFieldProps || {};
	const [selectedClassification, setSelectedClassification, onClassificationOptionChange] =
		useClassificationState(initialClassification);
	const createFormScrollRef = useCallback(
		(node: HTMLDivElement) => {
			if (!node) return;
			if (formMeta?.submitErrorMessage || hasCreatedAnotherIssue)
				scrollIntoViewIfNeeded(node, DEFAULT_SCROLL_OPTIONS);
		},
		[formMeta?.submitErrorMessage, hasCreatedAnotherIssue],
	);
	const resetSummary: React.MutableRefObject<ResetForm | null> = useRef(null);
	const formRef = useRef<HTMLFormElement>();
	const previousForgeFieldsState = useRef<InlineFieldsState | null>(null);
	const isRestoring = useRef(wasStateRestored);
	const [selectedTransition, setSelectedTransition] = useState<StatusTransition | null>(null);
	const onChangeSelectedTransition = (t: StatusTransition) => {
		const analyticsEvent = createAnalyticsEvent({});
		setSelectedTransition(t);
		fireTrackAnalyticsDeferred(analyticsEvent, 'statusInGic changed');

		publish(GIC_LIFECYCLE_EVENTS.STATUS_FIELD_STATE_CHANGE, {
			selectedTransition: t,
		});
	};
	const formSetFieldValue = useRef<(name: string, value: unknown) => void>();

	const { showEnforcementIfStorageIsFull } = useStorageEnforcement();

	const shouldShowStatus = useMemo(
		() => shouldShowStatusField(selectedProject?.projectType, fieldConfig),
		[selectedProject, fieldConfig],
	);

	const shouldShowLinkedAlerts = useMemo(
		() =>
			shouldShowLinkedAlertsField(
				selectedProject?.projectType,
				selectedIssueType,
				alertsToBeLinked,
			),
		[selectedProject?.projectType, selectedIssueType, alertsToBeLinked],
	);

	// NOTE: JPD has no ability to manage watchers at the time of release, this is a workaround to prevent watchers being added for JPD.
	const shouldShowWatchers = selectedProject?.projectType !== PRODUCT_DISCOVERY_PROJECT;

	const isCurrentUserAdminOfProject =
		useIsCurrentUserAdminOfProject({
			projectKey: selectedProject?.projectKey,
		}) ?? false;

	const canConfigureIssueTypes = canConfigureIssueTypeForProject({
		project: selectedProject,
		isProjectAdmin: isCurrentUserAdminOfProject,
	});

	const canConfigureStatuses = canConfigureStatusForProject({
		project: selectedProject,
		isProjectAdmin: isCurrentUserAdminOfProject,
	});

	const reloadForm = useCallback(
		(
			project: Project,
			issueType: IssueOrRequestType | null,
			includeIssueTypesAndRequestTypes: boolean,
		) => {
			const userInputForRetentionParams = {
				formState: getFormState.current(),
				fieldConfig,
				dirtyFieldsCurrentSession,
				retainedFieldsData,
				initialDefaultValues: triggerIssueCreatePayload?.defaultValues,
				selectedClassification,
			};
			const issueCreateTypeFilter = () => {
				const { issuelinks } = getFormState.current()?.values || {};

				return isModalLoadInContext &&
					isCreateLinkedIssue &&
					fg('create_linked_issues_trigger_point_issue_view')
					? {
							issueCreateType: isCreateLinkedIssue
								? ISSUE_CREATE_TYPE.linkedIssue
								: ISSUE_CREATE_TYPE.standard,
							additionalContext: {
								linkedIssue: {
									sourceIssueId:
										triggerIssueCreatePayload?.additionalContext?.linkedIssue?.sourceIssueId || '',
									includeLinks: issuelinks?.includeLinks,
									includeAttachments: issuelinks?.includeAttachments,
								},
							},
						}
					: {};
			};
			fetchConfig({
				variables: {
					...getQueryVariables(
						project,
						issueType,
						getUserInputForRetention(userInputForRetentionParams),
					),
					...(includeIssueTypesAndRequestTypes
						? {
								issueTypes: triggerIssueCreatePayload?.issueTypes,
								requestTypes: triggerIssueCreatePayload?.requestTypes,
							}
						: {}),
					...(isModalLoadInContext && {
						additionalContext: triggerIssueCreatePayload?.additionalContext,
					}),
					parentId: triggerIssueCreatePayload?.parentId,
					...issueCreateTypeFilter(),
				},
			});
		},
		[
			getFormState,
			fieldConfig,
			dirtyFieldsCurrentSession,
			fetchConfig,
			triggerIssueCreatePayload?.issueTypes,
			triggerIssueCreatePayload?.requestTypes,
			triggerIssueCreatePayload?.additionalContext,
			triggerIssueCreatePayload?.parentId,
			triggerIssueCreatePayload?.defaultValues,
			isModalLoadInContext,
			isCreateLinkedIssue,
			selectedClassification,
		],
	);

	const onProjectChange = useCallback(
		(project: Project): void => {
			setSubmitErrorMessage('');
			setSubmitFieldwiseErrors({});
			setSelectedProject(project);
			setSelectedIssueType(null);
			setSelectedTransition(null);
			setRequestBackedIssueType(null);
			setInlineErrorMessage(null);
			setShouldAutoFocus(false);
			if (fg('migrate_gic_smart_fields_jira_labs')) resetExpandedCollapsedArea();

			// when issue-type and request-type is null always honor request type
			shouldUseRequestTypeDefault.current = true;
			const userInputForRetentionParams = {
				formState: getFormState.current(),
				fieldConfig,
				dirtyFieldsCurrentSession,
				retainedFieldsData,
				initialDefaultValues: triggerIssueCreatePayload?.defaultValues,
				selectedClassification,
			};
			const issueCreateTypeFilter = () => {
				const { issuelinks } = getFormState.current()?.values || {};

				return isModalLoadInContext &&
					isCreateLinkedIssue &&
					fg('create_linked_issues_trigger_point_issue_view')
					? {
							issueCreateType: isCreateLinkedIssue
								? ISSUE_CREATE_TYPE.linkedIssue
								: ISSUE_CREATE_TYPE.standard,
							additionalContext: {
								linkedIssue: {
									sourceIssueId:
										triggerIssueCreatePayload?.additionalContext?.linkedIssue?.sourceIssueId || '',
									includeLinks: issuelinks?.includeLinks,
									includeAttachments: issuelinks?.includeAttachments,
								},
							},
						}
					: {};
			};
			fetchConfig({
				variables: {
					...getQueryVariables(
						project,
						null,
						getUserInputForRetention(userInputForRetentionParams),
					),
					parentId: triggerIssueCreatePayload?.parentId,
					...(isModalLoadInContext && {
						additionalContext: triggerIssueCreatePayload?.additionalContext,
					}),
					...issueCreateTypeFilter(),
				},
			});
			setIsEpicLinkSelected(false);
			// Reevaulate if forge fields are detected
			setIsForgeFieldsDetected(false);
			publish(GIC_LIFECYCLE_EVENTS.PROJECT_FIELD_VALUE_CHANGE, {
				project,
			});
		},
		[
			dirtyFieldsCurrentSession,
			fetchConfig,
			fieldConfig,
			getFormState,
			isCreateLinkedIssue,
			isModalLoadInContext,
			publish,
			setInlineErrorMessage,
			setIsEpicLinkSelected,
			setIsForgeFieldsDetected,
			setRequestBackedIssueType,
			setSelectedIssueType,
			setSelectedProject,
			setSubmitErrorMessage,
			setSubmitFieldwiseErrors,
			triggerIssueCreatePayload?.additionalContext,
			triggerIssueCreatePayload?.parentId,
			triggerIssueCreatePayload?.defaultValues,
			setShouldAutoFocus,
			resetExpandedCollapsedArea,
			selectedClassification,
		],
	);

	const onIssueTypeSelected = useCallback(
		(issueType: IssueOrRequestType): void => {
			if (selectedProject && issueType) {
				setSubmitErrorMessage('');
				setSubmitFieldwiseErrors({});
				setShouldAutoFocus(false);
				setRequestBackedIssueType(null);
				setInlineErrorMessage(null);
				if (fg('migrate_gic_smart_fields_jira_labs')) resetExpandedCollapsedArea();

				// when issue-type is non-null always honor issue type
				shouldUseRequestTypeDefault.current = issueType.type === REQUEST_TYPE;
				const userInputForRetentionParams = {
					formState: getFormState.current(),
					fieldConfig,
					dirtyFieldsCurrentSession,
					retainedFieldsData,
					initialDefaultValues: triggerIssueCreatePayload?.defaultValues,
					selectedClassification,
				};

				const payloadDefaultValues = () => {
					const { projectType } = selectedProject || {};
					if (projectType === SERVICE_DESK_PROJECT) {
						return isEmpty(getFormState.current()?.dirtyFields)
							? { defaultValues: triggerIssueCreatePayload?.defaultValues || {} }
							: {};
					}
					return {};
				};

				const issueCreateTypeFilter = () => {
					const { issuelinks } = getFormState.current()?.values || {};

					return isModalLoadInContext &&
						isCreateLinkedIssue &&
						fg('create_linked_issues_trigger_point_issue_view')
						? {
								issueCreateType: isCreateLinkedIssue
									? ISSUE_CREATE_TYPE.linkedIssue
									: ISSUE_CREATE_TYPE.standard,
								additionalContext: {
									linkedIssue: {
										sourceIssueId:
											triggerIssueCreatePayload?.additionalContext?.linkedIssue?.sourceIssueId ||
											'',
										includeLinks: issuelinks?.includeLinks,
										includeAttachments: issuelinks?.includeAttachments,
									},
								},
								...payloadDefaultValues(),
							}
						: {};
				};
				fetchConfig({
					variables: {
						...getQueryVariables(
							selectedProject,
							issueType,
							getUserInputForRetention(userInputForRetentionParams),
						),
						issueTypes: triggerIssueCreatePayload?.issueTypes,
						requestTypes: triggerIssueCreatePayload?.requestTypes,
						parentId: triggerIssueCreatePayload?.parentId,
						...(isModalLoadInContext && {
							additionalContext: triggerIssueCreatePayload?.additionalContext,
						}),
						...issueCreateTypeFilter(),
					},
				});
				setIsEpicLinkSelected(false);
				// Reevaulate if forge fields are detected
				setIsForgeFieldsDetected(false);
				publish(GIC_LIFECYCLE_EVENTS.ISSUE_TYPE_FIELD_VALUE_CHANGE, {
					issueType,
				});
			}
		},
		[
			selectedProject,
			setSubmitErrorMessage,
			setSubmitFieldwiseErrors,
			setRequestBackedIssueType,
			setInlineErrorMessage,
			getFormState,
			fieldConfig,
			dirtyFieldsCurrentSession,
			fetchConfig,
			triggerIssueCreatePayload?.issueTypes,
			triggerIssueCreatePayload?.requestTypes,
			triggerIssueCreatePayload?.parentId,
			triggerIssueCreatePayload?.additionalContext,
			triggerIssueCreatePayload?.defaultValues,
			isModalLoadInContext,
			setIsEpicLinkSelected,
			setIsForgeFieldsDetected,
			publish,
			isCreateLinkedIssue,
			setShouldAutoFocus,
			resetExpandedCollapsedArea,
			selectedClassification,
		],
	);

	const project = contextFields.project?.selectedProject;
	const issueType = contextFields.issuetype?.selectedIssueType;
	const requestType = contextFields.requestType?.selectedRequestType;
	const inlineError = useMemo(() => transformInlineErrorMessage(sectionMessage), [sectionMessage]);

	useEffect(() => {
		publish(GIC_LIFECYCLE_EVENTS.CREATE_FORM_FIELD_CONFIG_UPDATE, {
			fieldConfig,
		});
	}, [publish, fieldConfig]);

	useEffect(() => {
		if (areConfiguredFieldsModified && selectedProject && selectedIssueType) {
			setAreConfiguredFieldsModified(false);
			const userInputForRetentionParams = {
				formState: getFormState.current(),
				fieldConfig,
				dirtyFieldsCurrentSession,
				retainedFieldsData,
				initialDefaultValues: triggerIssueCreatePayload?.defaultValues,
				selectedClassification,
			};
			const issueCreateTypeFilter = () => {
				const { issuelinks } = getFormState.current()?.values || {};

				return isModalLoadInContext &&
					isCreateLinkedIssue &&
					fg('create_linked_issues_trigger_point_issue_view')
					? {
							issueCreateType: isCreateLinkedIssue
								? ISSUE_CREATE_TYPE.linkedIssue
								: ISSUE_CREATE_TYPE.standard,
							additionalContext: {
								linkedIssue: {
									sourceIssueId:
										triggerIssueCreatePayload?.additionalContext?.linkedIssue?.sourceIssueId || '',
									includeLinks: issuelinks?.includeLinks,
									includeAttachments: issuelinks?.includeAttachments,
								},
							},
						}
					: {};
			};
			fetchConfig({
				variables: {
					...getQueryVariables(
						selectedProject,
						selectedIssueType,
						getUserInputForRetention(userInputForRetentionParams),
					),
					parentId: triggerIssueCreatePayload?.parentId,
					...(isModalLoadInContext && {
						additionalContext: triggerIssueCreatePayload?.additionalContext,
					}),
					...issueCreateTypeFilter(),
				},
			});
			setIsEpicLinkSelected(false);
		}
	}, [
		isModalLoadInContext,
		fetchConfig,
		fieldConfig,
		selectedProject,
		selectedIssueType,
		areConfiguredFieldsModified,
		setAreConfiguredFieldsModified,
		dirtyFieldsCurrentSession,
		retainedFieldsData,
		setIsEpicLinkSelected,
		triggerIssueCreatePayload?.parentId,
		triggerIssueCreatePayload?.additionalContext,
		triggerIssueCreatePayload?.defaultValues,
		getFormState,
		isCreateLinkedIssue,
		selectedClassification,
	]);

	useEffect(() => {
		if (isRestoring.current) {
			/**
			 * Wait for the field config to load before setting isRestoring to false
			 * otherwise we will set it false before the project field config loads,
			 * so once the field config loads we dont skip sine isRestoring would be false
			 */
			if (project || issueType) {
				isRestoring.current = false;
			}
			return;
		}

		if (project) {
			setSelectedProject(project);
		}

		if (issueType) {
			setSelectedIssueType(
				shouldUseRequestTypeDefault.current === true && requestType ? requestType : issueType,
			);
			setRequestBackedIssueType(
				shouldUseRequestTypeDefault.current === true && requestType ? issueType : null,
			);
		}
	}, [
		issueType,
		project,
		requestType,
		setSelectedIssueType,
		setSelectedProject,
		setRequestBackedIssueType,
	]);

	useEffect(() => {
		const newMeta = { ...formMeta, ...meta };
		if (!isEqual(formMeta, newMeta)) {
			setMeta(newMeta);
		}
	}, [formMeta, meta, setMeta]);

	const { initialTransition, preselectedTransition } = statusFieldProps || {};

	useEffect(() => {
		setSelectedTransition(preselectedTransition || initialTransition || null);
	}, [initialTransition, preselectedTransition, setSelectedTransition]);

	useEffect(() => {
		setSelectedClassification(initialClassification || undefined);
	}, [initialClassification, setSelectedClassification]);

	// for error handling we need to set dataFetchError in formContext
	useEffect(() => {
		setDataFetchError(error);
	}, [error, setDataFetchError]);

	useEffect(() => {
		setInlineErrorMessage(inlineError);
	}, [inlineError, setInlineErrorMessage]);

	useEffect(() => {
		if (presetSubmitErrorMessage) {
			setSubmitErrorMessage(presetSubmitErrorMessage);
		}
	}, [presetSubmitErrorMessage, setSubmitErrorMessage]);

	useDescriptionAnalytics();

	const currentUIModificationsConsentState = useUIModificationsConsentStatus();

	const onUIModificationsConsentRequiredMount = (analyticsEvent: UIAnalyticsEvent) =>
		fireOperationalAnalyticsDeferred(
			analyticsEvent,
			'jiraUiModificationsObligatoryConsent gicHidden',
		);

	const areForgeInlineFieldsStoredOrIdle = useCallback(
		() =>
			Object.values(fields).every(
				(fieldData: InlineFieldState) =>
					fieldData.status === STORED_FIELD_STATE || fieldData.status === IDLE_FIELD_STATE,
			),
		[fields],
	);

	const isForgeInlineFieldsError = useCallback(
		() =>
			Object.values(fields).some(
				(fieldData: InlineFieldState) => fieldData.status === FAILED_FIELD_STATE,
			),
		[fields],
	);

	const showInvitePrompt = useInvitePrompt(selectedProject?.projectKey);

	const sendCrossJoinThirdPartyNudge = useCrossJoinThirdPartyNudgeExperiment();

	const onCreateSuccess: OnCreateSuccess = ({
		issueCreateResponse,
		issueCreateFieldsUsed,
		issueCreateContextUsed,
		telemetryAttributes = {},
		collapsedFieldsLength,
		issueType: issueTypeName,
	}: IssueCreateSuccessProps) => {
		const { key: issueKey, id: issueId, watchers: watchersResponse } = issueCreateResponse;

		onIssueCreateSuccess(
			{ issueKey, issueId: Number(issueId), issueType: issueTypeName },
			shouldShowWatchers ? watchersResponse : undefined,
		);

		sendCrossJoinThirdPartyNudge();

		showInvitePrompt(getFormState.current()?.values?.description);
		setSubmitting(false);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const boardId = getBoardId(window.location.href);
		const analyticsEvent = createAnalyticsEvent({});

		const attributes: Attributes = {
			sessionId,
			issueCreateFieldsUsed,
			issueCreateContextUsed,
			boardId: boardId !== null ? Number(boardId) : undefined,
			projectType: selectedProject?.projectType,
			issueType: getAnalyticsIssueType(selectedIssueType),
			...(!!selectedIssueType?.value && {
				issueTypeId: selectedIssueType?.value,
			}),
			...(selectedIssueType?.type === REQUEST_TYPE
				? {
						hasRequestType: true,
						requestTypeKey: selectedIssueType?.requestTypeKey,
					}
				: {
						hasRequestType: false,
					}),
			triggerPointKey,
			...(shouldShowWatchers && {
				watchers: {
					count: watchers?.length,
					used: isWatchersUsed({
						watchers,
						isAutoWatchEnabled: watchersConfigurations?.isAutoWatchEnabled,
						currentUserAccountId: currentUser.accountId,
					}),
					...watchersConfigurations,
				},
			}),
			...(shouldShowLinkedAlerts && {
				linkedAlerts: alertsToBeLinked,
			}),
			edition:
				selectedProject?.projectType &&
				getEditionForProject(selectedProject.projectType, appEditions),
			collapsedFieldsLength,
			...(fg('default_assignee_to_issue_creator')
				? {
						assignee: {
							assigneeDefault: getAssigneeType(
								fieldConfig?.assignee?.defaultValue?.accountId,
								currentUser,
							),
							assigneeCreated: getAssigneeType(
								getFormState.current()?.values?.assignee?.accountId,
								currentUser,
							),
						},
					}
				: {}),
		};

		if (shouldShowStatus) {
			attributes.statusInGicEnabled = true;
			if (selectedTransition && !selectedTransition.isInitial) {
				attributes.userChangedStatusTo = selectedTransition.to?.statusCategory?.name;
			}
		}

		if (isChildIssueTriggerPoint(triggerPointKey)) {
			const { parent } = getFormState.current()?.values || {};
			attributes.parentId = parent?.key;
		}

		fireTrackAnalyticsDeferred(analyticsEvent, 'issue created', attributes);

		fireOperationalAnalyticsDeferred(createAnalyticsEvent({}), 'ui taskSuccess', {
			task: 'createIssue',
			isNewGIC: true,
			isCreated: true,
			triggerPointKey,
			edition:
				selectedProject?.projectType &&
				getEditionForProject(selectedProject.projectType, appEditions),
			application:
				selectedProject?.projectType && getApplicationForProject(selectedProject.projectType),
			...telemetryAttributes,
		});
	};

	const findFirstTabIndexWithErrors = useCallback(
		(errors = {}) => {
			const index = fieldTabs.findIndex((tab) =>
				tab.items.find((item) => errors && item === Object.keys(errors)[0]),
			);
			return index > -1 ? index : 0;
		},
		[fieldTabs],
	);

	const onCreateFailure: OnCreateFailure = ({
		issueCreateError: err,
		telemetryAttributes,
		issueType: issueTypeLabel,
	}) => {
		setSubmitting(false);

		// on 4xx
		if (isValidationError(err)) {
			const { message } = err;
			if (message && message.length) {
				setSubmitErrorMessage(message.split('; ').join('\r\n'));
			}

			const errors = 'errors' in err ? transformValidationErrors(err.errors) : undefined;
			setSubmitFieldwiseErrors(errors);
			setCurrentActiveTabIndex(findFirstTabIndexWithErrors(errors));

			const customFieldError = isDisplayConditionCFCausingError(
				'errors' in err ? err.errors : [],
				fieldConfig,
				filteredOutModules,
			);
			const issueTypeName = issueTypeLabel?.label;
			const getSingleFieldErrorMsg = () => {
				if (issueType && expVal('jira-itr-work-type-exp-1', 'isEnabled', false)) {
					return messages.singleRequiredCustomFieldIssueCreationBlockIssueTerminologyRefreshM4;
				}
				return expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.singleRequiredCustomFieldIssueCreationBlockIssueTermRefresh
					: messages.singleRequiredCustomFieldIssueCreationBlock;
			};

			const getMultipleFieldErrorMsg = () => {
				if (issueType && expVal('jira-itr-work-type-exp-1', 'isEnabled', false)) {
					return messages.multipleRequiredCustomFieldsIssueCreationBlockIssueTerminologyRefreshM4;
				}
				return expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.multipleRequiredCustomFieldsIssueCreationBlockIssueTermRefresh
					: messages.multipleRequiredCustomFieldsIssueCreationBlock;
			};

			if (customFieldError) {
				const errorMessage =
					customFieldError.length > 1
						? formatMessage(getMultipleFieldErrorMsg(), {
								fieldsName: formatList(customFieldError, {
									style: 'long',
									type: 'conjunction',
								}),
								issueType: issueTypeName,
							})
						: formatMessage(getSingleFieldErrorMsg(), {
								fieldName: formatList(customFieldError, {
									style: 'short',
									type: 'conjunction',
								}),
								issueType: issueTypeName,
							});
				setSubmitErrorMessage(String(errorMessage));
			}

			return errors;
		}

		// on 5xx
		let description;
		if (isEmbedded) {
			description = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
				? messages.embeddedCreateErrorFlagDescriptionIssueTermRefresh
				: messages.embeddedCreateErrorFlagDescription;
		} else {
			description = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
				? messages.createErrorFlagDescriptionIssueTermRefresh
				: messages.createErrorFlagDescription;
		}
		showFlag({
			type: 'error',
			title: expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
				? messages.createErrorFlagTitleIssueTermRefresh
				: messages.createErrorFlagTitle,
			description,
			testId: 'jira-issue-create.modal.create-form.error-flag',
			messageId: 'issue-create.ui.modal.create-form.show-flag.error',
			messageType: 'transactional',
		});

		fireOperationalAnalyticsDeferred(createAnalyticsEvent({}), 'ui taskFail', {
			task: 'createIssue',
			isNewGIC: true,
			isCreated: true,
			triggerPointKey,
			edition:
				selectedProject?.projectType &&
				getEditionForProject(selectedProject.projectType, appEditions),
			application:
				selectedProject?.projectType && getApplicationForProject(selectedProject.projectType),
			...telemetryAttributes,
		});
		return undefined;
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onSubmit = async (formData: any, formApi: FormApi<any>): Promise<FormError> => {
		if (selectedProject && selectedIssueType) {
			setSubmitting(true);
			setSubmitErrorMessage('');
			setSubmitFieldwiseErrors({});

			const telemetryAttributes: TelemetryAttributes = {
				wasIssueCreatedWithCreateAnotherIssue: hasCreatedAnotherIssue,
				view: minimizableModalView,
			};

			if (isForgeInlineFieldsError()) {
				const forgeInlineError = new ValidationError(
					'',
					Object.keys(fields).map((key) => ({
						field: key,
						error: formatMessage(messages.createErrorForgeInlineField),
					})),
				);
				return onCreateFailure({
					issueCreateError: forgeInlineError,
					telemetryAttributes,
					issueType: selectedIssueType,
				});
			}

			const isForgeFieldFocused = Object.values(fields).some(
				(field) => field.status === FOCUSED_FIELD_STATE,
			);

			const isForgeIframe = (el: HTMLElement) => el.dataset?.forgeIframe === 'true';

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const activeElement = window.document?.activeElement;

			const isForgeIframeActive =
				activeElement instanceof HTMLElement && isForgeIframe(activeElement);

			// When the user is focused on a Forge field when clicking on "submit" button in GIC
			// we need to manually trigger focus event on form button and block the form submission
			// because the fields rely on blur events to submit the value. For the UI kit fields
			// we rely on isForgeFieldFocused value, however for the custom ui fields we don't change
			// the fields state on focus events so we need to check it with isForgeIframeActive
			const shouldFocusFormButton = isForgeFieldFocused || isForgeIframeActive;

			if (
				shouldFocusFormButton ||
				(!areForgeInlineFieldsStoredOrIdle() && forgeFieldBlockStatus !== FORGE_STATUS_TIMEOUT)
			) {
				if (shouldFocusFormButton) {
					previousForgeFieldsState.current = fields;
					focusFormButton(ISSUE_CREATE_FORM);
				}
				setForgeFieldBlockStatus(FORGE_STATUS_WAIT);
				const timeout = setTimeout(() => {
					setForgeFieldBlockStatus(FORGE_STATUS_TIMEOUT);
					clearTimeout(timeout);
				}, FORGE_CF_TIMEOUT);
				return undefined;
			}

			try {
				const payload: CreateIssuePayload = {
					formData: {
						...getContextFieldsForSubmit(
							selectedProject,
							selectedIssueType,
							contextFields.requestType?.fieldId,
						),
						...getParentIdForSubmit(triggerIssueCreatePayload?.parentId),
						...formData,
					},
					onSuccess: (issueCreateSuccessProps) =>
						onCreateSuccess({
							...issueCreateSuccessProps,
							telemetryAttributes,
							issueType: selectedIssueType?.label,
						}),
					fieldConfig,
					...(fg('migrate_gic_smart_fields_jira_labs') && {
						fieldTabs,
						hasIssueAdjustmentModules: uiModificationsStatus === 'enabled',
					}),
					...(canSetContextForSubmit(selectedProject, context) && { context }),
					...(fg('create_linked_issues_trigger_point_issue_view') && {
						createLinkedIssuePayload: {
							isCreateLinkedIssue,
							sourceIssueKey: isCreateLinkedIssue
								? triggerIssueCreatePayload?.defaultValues?.issueLinks?.linkedIssueKeys[0]
								: null,
						},
					}),
					isSubTaskCreationOpen,
					triggerPointKey,
					sessionId,
					interactedFields,
					shouldShowStatus,
				};
				try {
					if (selectedClassification) {
						payload.formData.dataclassification = {
							id: selectedClassification?.id
								? PlatformClassificationTagAri.parse(selectedClassification.id)?.tagId
								: null,
						};
					}
				} catch (e) {
					// do nothing
				}

				if (shouldShowStatus && selectedTransition && !selectedTransition.isInitial) {
					payload.transition = { id: selectedTransition.id };
				}

				if (shouldShowWatchers && watchersConfigurations?.canManageWatchers) {
					payload.watchers = watchers?.map((watcher) => watcher.accountId);
					payload.skipAutoWatch = true;
				}

				await createIssue(payload);
				setHasCreatedAnotherIssue(false);

				const issueTypeName = selectedIssueType.label;

				if (forgeFieldBlockStatus === FORGE_STATUS_TIMEOUT) {
					const timedoutCFS: string[] = [];
					Object.entries(fields).forEach(([key, value]) => {
						if (value.status === PENDING_FIELD_STATE) {
							timedoutCFS.push(fieldConfig[key].fieldName);
						}
					});
					const getTitle = () => {
						if (issueType && expVal('jira-itr-work-type-exp-1', 'isEnabled', false)) {
							return messages.timeoutForgeInlineFieldTitleIssueTerminologyRefreshM4;
						}

						return expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.timeoutForgeInlineFieldTitleIssueTermRefresh
							: messages.timeoutForgeInlineFieldTitle;
					};
					const getDesc = () => {
						if (issueType && expVal('jira-itr-work-type-exp-1', 'isEnabled', false)) {
							return messages.timeoutForgeInlineFieldDescriptionIssueTerminologyRefreshM4;
						}

						return expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.timeoutForgeInlineFieldDescriptionIssueTermRefresh
							: messages.timeoutForgeInlineFieldDescription;
					};
					if (timedoutCFS.length) {
						showFlag({
							type: 'warning',
							title: [getTitle(), { issueType: issueTypeName }],
							description: [
								getDesc(),
								{
									fields: timedoutCFS.join(', '),
									bold: (str) => <b>{str}</b>,
									issueType: issueTypeName,
								},
							],
							testId: 'jira-issue-create.modal.create-form.forge-timeout-flag',
							messageId: 'issue-create.ui.modal.create-form.show-flag.warning',
							messageType: 'transactional',
						});
					}
				}
				if (canCreateAnotherIssue) {
					setShouldAutoFocus(true);
					sectionMessage && clearSectionMessage();
					setTimeout(() => {
						// reset is used instead of setFieldValue because we want to reset the summary field to it's pristine state. setFieldValue only sets the value and the fields remains dirty causing a validation error when create another issue option is checked
						// resetSummary.current({
						//     ...formData,
						//     summary: '',
						// });
						const fieldsReset = resetFormFieldsForCreateAnother(
							formData,
							fieldConfig,
							formApi.reset,
						);

						publish(GIC_LIFECYCLE_EVENTS.RESET_FORM_FIELDS_FOR_CREATE_ANOTHER, {
							fieldsReset: fieldsReset ?? {},
						});

						setSelectedTransition(statusFieldProps?.initialTransition || null);

						setSelectedClassification(classificationFieldProps?.initialClassification || undefined);

						onFormFieldsReset?.();

						setHasCreatedAnotherIssue(true);
					}, 0);
				}
				// Show enforcement if created JSW issue and it was a success
				const isSoftwareProjectSelected = selectedProject.projectType === 'software';

				if (isSoftwareProjectSelected) {
					showEnforcementIfStorageIsFull({
						delayShow: minimizableModalView === 'modal' && canCreateAnotherIssue,
						// We only care if the issue was created for a JSW project or not
						// rather than if the user is currently viewing a JSW project
						shouldOnlyShowInJSWContext: false,
						touchpoint: 'global-issue-create',
					});
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				return onCreateFailure({
					issueCreateError: err,
					telemetryAttributes,
					issueType: selectedIssueType,
				});
			}
		}
		resetState();
		return undefined;
	};

	useEffect(() => {
		if (forgeFieldBlockStatus !== FORGE_STATUS_PROCEED) {
			const isTimeout = forgeFieldBlockStatus === FORGE_STATUS_TIMEOUT;
			// for CustomUI inline edit we need to compare fieldState in case of forced blur effect
			const customUIFieldStateCheck = previousForgeFieldsState.current !== fields;
			if ((areForgeInlineFieldsStoredOrIdle() && customUIFieldStateCheck) || isTimeout) {
				previousForgeFieldsState.current = fields;
				setForgeFieldBlockStatus(FORGE_STATUS_PROCEED);
				submitForgeFormField(formRef);
			} else {
				dispatchFormSubmits();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [areForgeInlineFieldsStoredOrIdle, getFormState, forgeFieldBlockStatus, selectedTransition]);

	useEffect(() => {
		if (isSmartFieldsToggled === true) {
			selectedProject && reloadForm(selectedProject, selectedIssueType, false);
			setIsSmartFieldsToggled(false);
		}
	}, [
		isSmartFieldsToggled,
		reloadForm,
		selectedIssueType,
		selectedProject,
		setIsSmartFieldsToggled,
	]);

	const onIssueCreateFormMount = (analyticsEvent: UIAnalyticsEvent) =>
		fireOperationalAnalyticsDeferred(analyticsEvent, 'IssueCreateForm rendered');

	const handleFormDirty = (dirty: boolean): void => {
		let isDirty = dirty;
		if (
			dirtyFieldsCurrentSession &&
			dirtyFieldsCurrentSession.current &&
			Object.keys(dirtyFieldsCurrentSession.current).length
		) {
			isDirty = true;
		}

		setIsFormDirty(isDirty);
	};

	const getForm = () => {
		if (currentUIModificationsConsentState === UIM_CONSENT_REQUIRED) {
			resetSummary.current = null;
			return <MountEvent onMount={onUIModificationsConsentRequiredMount} />;
		}

		if (!isLoading && meta.queryErrorMessage) {
			resetSummary.current = null;
			return null;
		}

		if (isLoading || !(selectedProject && selectedIssueType)) {
			resetSummary.current = null;
			return <IssueCreateLoader skeletonCount={6} />;
		}

		if (fieldConfig && !meta.queryErrorMessage) {
			return (
				<>
					<AKForm
						key={`${selectedProject.value}-${selectedIssueType.value}`}
						onSubmit={onSubmit}
						isDisabled={!meta.areRequiredFieldsSupported}
					>
						{({ formProps, getState, dirty, reset, setFieldValue }) => {
							getFormState.current = getState;
							formSetFieldValue.current = setFieldValue;
							/**
							 * isFormDirty is the actual value we should use to tell if the form
							 * is dirty. isFormDirty comes from form-context, and is updated
							 * in handleFormDirty.
							 *
							 * We pass the AKForm's dirty so that handleFormDirty can use both
							 * AKForm's dirty and dirtyFieldsCurrentSession (from props) to set
							 * isFormDirty correctly by taking both into account.
							 *
							 * Note - we are using this isFormDirty in issue-create-restore to block nav there
							 * src/packages/issue-create/issue-create-restore/src/controllers/should-show-browser-warning/index.tsx
							 */
							handleFormDirty(dirty);
							if (!resetSummary.current) {
								resetSummary.current = reset;
								setResetFormAction(reset);
							}

							const getLayoutRenderer = () => (
								<LayoutRenderer
									fieldConfig={fieldConfig}
									fieldTabs={fieldTabs}
									setFieldValue={setFieldValue}
									getFormState={getState}
									apiLatency={apiLatency}
									fieldPropsExtensions={
										isGICPositioningEnabled() ? fieldPropsExtensions : undefined
									}
									optOutSmartFieldIds={
										optOutSmartFieldIds && fg('opt_out_smart_fields_in_gic')
											? optOutSmartFieldIds
											: undefined
									}
								/>
							);

							return (
								<>
									<WrapperDiv>
										<form
											{...formProps}
											id="issue-create.ui.modal.create-form"
											ref={(ref) => {
												// @ts-expect-error - TS2322 - Type 'HTMLFormElement | null' is not assignable to type 'HTMLFormElement | undefined'.
												formRef.current = ref;
											}}
											noValidate
										>
											<FormApiProvider getFormState={getState} setFieldValue={setFieldValue}>
												{getLayoutRenderer()}
											</FormApiProvider>
										</form>
									</WrapperDiv>
								</>
							);
						}}
					</AKForm>
					<MountEvent onMount={onIssueCreateFormMount} />
				</>
			);
		}
		return null;
	};

	const onIssueTypeCreatedOrUpdated = () => {
		if (selectedProject) reloadForm(selectedProject, selectedIssueType, true);
	};
	const getRequestTypeFieldsLayoutPreference = () =>
		// if we have request types (JSM CMP only) then we want to show the toggle
		contextFields.requestType?.requestTypes && (
			<RequestTypeFieldsLayoutPreference
				isRequestTypeSelected={selectedIssueType?.type === REQUEST_TYPE}
				onChange={() => selectedProject && reloadForm(selectedProject, selectedIssueType, false)}
				overrideIsEnabled={overrideUseRequestTypeFieldsToggleState}
			/>
		);

	const getProformaBanner = () =>
		!isLoading &&
		proformaConfiguration?.hasProformaForm && (
			<ProformaBanner portalLink={proformaConfiguration.portalLink} />
		);

	const getClassificationField = () =>
		classificationFieldProps?.availableClassifications?.length &&
		classificationFieldProps.availableClassifications.length > 0 ? (
			<DataClassificationField
				allowedValues={classificationFieldProps?.availableClassifications || []}
				value={selectedClassification}
				onChange={onClassificationOptionChange}
				width={isMini ? '100%' : undefined}
				nonEditableReason={classificationFieldProps.nonEditableMessage}
				{...classificationFieldProps}
				errorMessage={formMeta?.submitFieldwiseErrors?.dataclassification}
			/>
		) : null;

	// memoise the issueTypes value so that a new `[]` is not passed into TypePicker per render
	const issueTypes = contextFields.issuetype?.issueTypes;
	const issueTypesMemoised = useMemo(() => issueTypes || [], [issueTypes]);

	const projectPickerIsDisabled = isSubTaskCreationOpen || disableProjectDropdown;

	const projectProps = useMemo(
		() => ({
			projectKey: selectedProject?.projectKey ?? '',
			projectId: selectedProject?.value ?? '',
			projectType: selectedProject?.projectType ?? null,
		}),
		[selectedProject],
	);

	const inlineConfigStatusProps = useMemo(
		() => ({
			...projectProps,
			issueTypeId: selectedIssueType?.value,
			canConfigureStatuses,
			onStatusCreatedOrUpdated,
		}),
		[canConfigureStatuses, onStatusCreatedOrUpdated, projectProps, selectedIssueType?.value],
	);

	const oldContent = (
		<>
			<ProjectPicker
				onChange={onProjectChange}
				value={selectedProject}
				renderOptionsLimit={25}
				skipFetchProjects={!!meta.queryErrorMessage}
				disableFetchProject={projectPickerIsDisabled}
				isDisabled={projectPickerIsDisabled}
				width={isMini ? '100%' : undefined}
				onProjectSelection={onProjectSelection}
			/>
			{isMini &&
			selectedProject?.projectType !== SERVICE_DESK_PROJECT &&
			shouldShowStatus &&
			statusFieldProps ? (
				<MiniIssueTypeStatusWrapper>
					<TypePicker
						onIssueTypeCreatedOrUpdated={onIssueTypeCreatedOrUpdated}
						canConfigureIssueTypes={canConfigureIssueTypes}
						issueTypes={issueTypesMemoised}
						requestTypes={contextFields.requestType?.requestTypes}
						onChange={onIssueTypeSelected}
						value={selectedIssueType}
						isDisabled={disableIssueTypeDropdown}
						{...projectProps}
					/>
					<StatusField
						isLoading={!!statusFieldProps.isTransitionsLoading}
						error={statusFieldProps.transitionsError}
						transitions={statusFieldProps.availableTransitions || []}
						isMini
						value={selectedTransition}
						onChange={onChangeSelectedTransition}
						{...inlineConfigStatusProps}
					/>
				</MiniIssueTypeStatusWrapper>
			) : (
				<>
					<TypePicker
						onIssueTypeCreatedOrUpdated={onIssueTypeCreatedOrUpdated}
						canConfigureIssueTypes={canConfigureIssueTypes}
						issueTypes={issueTypesMemoised}
						requestTypes={contextFields.requestType?.requestTypes}
						onChange={onIssueTypeSelected}
						value={selectedIssueType}
						isDisabled={disableIssueTypeDropdown}
						{...projectProps}
						isMini={isMini}
					/>
					<Divider />
					{shouldShowStatus && statusFieldProps && (
						<StatusField
							isLoading={!!statusFieldProps.isTransitionsLoading}
							error={statusFieldProps.transitionsError}
							transitions={statusFieldProps.availableTransitions || []}
							isMini={false}
							value={selectedTransition}
							onChange={onChangeSelectedTransition}
							{...inlineConfigStatusProps}
						/>
					)}
				</>
			)}
			{getRequestTypeFieldsLayoutPreference()}
			{getClassificationField()}
			{shouldShowLinkedAlerts && (
				<LinkedAlertsField
					value={alertsToBeLinked || []}
					onRemoveButtonClick={removeAlertFromAlertsToBeLinked}
				/>
			)}
			{getProformaBanner()}
			<ExternalControlsRenderer
				externalControls={ExternalControls}
				setFieldValue={formSetFieldValue.current}
				getFormState={getFormState.current}
			/>
			{getForm()}
		</>
	);

	const hasCollapsedFields = useMemo(
		() => fieldTabs.some((tab) => !!tab.collapsedItems?.length),
		[fieldTabs],
	);

	// Move this into the return statement when cleaning up gic.positioning
	const newContent = (
		<RelativeWrapper>
			<FlexWrapper>
				{!isMini && isVisualRefreshEnabled() ? (
					<Text as="span" size="medium" color="color.text.subtlest" aria-hidden="true">
						{formatMessage(messages.requiredFieldsSummary)} <RequiredAsterisk />
					</Text>
				) : (
					<RequiredMessage aria-hidden="true" isMini={!!isMini}>
						{formatMessage(messages.requiredFieldsSummary)} <RequiredAsterisk />
					</RequiredMessage>
				)}
				{displayHeaderActionContainer && !isVisualRefreshEnabled() && (
					<ActionsContainer
						watchersConfigurations={watchersConfigurations}
						fieldConfig={fieldConfig}
					/>
				)}
			</FlexWrapper>

			{displaySitePicker && (
				<SitePicker
					onChange={onResetApolloCache}
					width={isMini ? '100%' : undefined}
					onSiteSelection={onSiteSelection}
				/>
			)}
			<ProjectPicker
				onChange={onProjectChange}
				value={selectedProject}
				renderOptionsLimit={25}
				skipFetchProjects={!!meta.queryErrorMessage}
				disableFetchProject={projectPickerIsDisabled}
				isDisabled={projectPickerIsDisabled}
				width={isMini ? '100%' : undefined}
				onProjectSelection={onProjectSelection}
			/>
			{minimizableModalView === 'mini' &&
			selectedProject?.projectType !== SERVICE_DESK_PROJECT &&
			shouldShowStatus &&
			statusFieldProps ? (
				<>
					<MiniIssueTypeStatusWrapper>
						<TypePicker
							onIssueTypeCreatedOrUpdated={onIssueTypeCreatedOrUpdated}
							canConfigureIssueTypes={canConfigureIssueTypes}
							issueTypes={issueTypesMemoised}
							requestTypes={contextFields.requestType?.requestTypes}
							onChange={onIssueTypeSelected}
							value={selectedIssueType}
							isDisabled={disableIssueTypeDropdown}
							{...projectProps}
						/>
						<StatusField
							isLoading={!!statusFieldProps.isTransitionsLoading}
							error={statusFieldProps.transitionsError}
							transitions={statusFieldProps.availableTransitions || []}
							isMini
							value={selectedTransition}
							onChange={onChangeSelectedTransition}
							{...inlineConfigStatusProps}
						/>
					</MiniIssueTypeStatusWrapper>
					{uiModificationsStatus !== 'enabled' && hasCollapsedFields && (
						<Box xcss={smartFieldMessageWrapperStyles}>
							<Divider />
							<SmartFieldMessage />
						</Box>
					)}
				</>
			) : (
				<>
					<TypePicker
						onIssueTypeCreatedOrUpdated={onIssueTypeCreatedOrUpdated}
						canConfigureIssueTypes={canConfigureIssueTypes}
						issueTypes={issueTypesMemoised}
						requestTypes={contextFields.requestType?.requestTypes}
						onChange={onIssueTypeSelected}
						value={selectedIssueType}
						isDisabled={disableIssueTypeDropdown}
						{...projectProps}
						isMini={isMini}
					/>
					<Divider />
					{uiModificationsStatus !== 'enabled' && hasCollapsedFields && <SmartFieldMessage />}
					{shouldShowStatus && statusFieldProps && (
						<StatusField
							isLoading={!!statusFieldProps.isTransitionsLoading}
							error={statusFieldProps.transitionsError}
							transitions={statusFieldProps.availableTransitions || []}
							isMini={false}
							value={selectedTransition}
							onChange={onChangeSelectedTransition}
							{...inlineConfigStatusProps}
						/>
					)}
				</>
			)}
			{getRequestTypeFieldsLayoutPreference()}
			{getClassificationField()}
			{shouldShowLinkedAlerts && (
				<LinkedAlertsField
					value={alertsToBeLinked || []}
					onRemoveButtonClick={removeAlertFromAlertsToBeLinked}
				/>
			)}
			{getProformaBanner()}
			<ExternalControlsRenderer
				externalControls={ExternalControls}
				setFieldValue={formSetFieldValue.current}
				getFormState={getFormState.current}
			/>
			{getForm()}
		</RelativeWrapper>
	);

	useEffect(() => {
		let forgeCustomFieldDetected = false;
		Object.values(fieldConfig).forEach((field) => {
			if (isForgeCustomField(field.fieldType)) {
				forgeCustomFieldDetected = true;
			}
		});
		setIsForgeFieldsDetected(forgeCustomFieldDetected);
	}, [fieldConfig, setIsForgeFieldsDetected]);

	return (
		<div ref={createFormScrollRef}>
			<div id={FORM_DROPZONE_CONTAINER_ID}>
				<SectionMessages />
				{isGICPositioningEnabled() ? newContent : oldContent}
			</div>
		</div>
	);
};

const CreateForm = memo<CreateFormProps>(CreateFormBase);

export default CreateForm;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WrapperDiv = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	form: {
		display: 'contents',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.akEditor + div': {
		display: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RelativeWrapper = styled.div({
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MiniIssueTypeStatusWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'baseline',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> :first-child': {
		marginRight: token('space.100'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RequiredMessage = styled.p<{ isMini: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	font: ({ isMini }) => (isMini ? token('font.body.UNSAFE_small') : token('font.body')),
	color: token('color.text.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

const smartFieldMessageWrapperStyles = xcss({
	marginBottom: 'space.300',
});
